import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { ApolloProvider } from '@apollo/client'
import { client } from '../infra/apollo/client'
import { RoutingProvider } from '../routing/RoutingProvider'
import { LegacyPageProvider, buildFromRoot } from '../infra/legacy/LegacyPageProvider'


export const removeBinding = (els: NodeListOf<Element>) => {
  els.forEach(el => {
    el.removeAttribute('data-react-bind')
  })
}

const DesignateBound = ({ element, isRoot }: { element: Element, isRoot: boolean }): JSX.Element => {
  useEffect(() => {
    element.removeAttribute('data-react-bind')
    element.setAttribute('data-react-bound', 'true')
    if (isRoot) {
      element.classList.add('v2-shim')
      document.body.parentElement?.classList.remove('legacy-styling')
    } else {
      element.classList.add('tailwind-scope')
    }
  }, [element, isRoot])
  return <></>
}

export const makeBinding = (
  loaders: () => [boolean, NodeListOf<Element>, JSX.Element | 'root'],
) => () => { 
  const [condition, element, Component] = loaders()
  const roots = element
  if (!condition) {
    removeBinding(roots)
    return
  }
  if (!roots) {
    console.error('Missing page-wrapper')
    return
  }
  if (roots.length !== 1) {
    console.error('There should be only one page-wrapper')
    return
  }

  const root = roots[0]

  if (Component === 'root' && root.getAttribute('data-react-root')) {
    return
  }

  if (Component === 'root' && window.self !== window.top) {
    removeBinding(roots)
    root.classList.add('iframe-embed')
    window.document.querySelector('.sidebar')?.remove()
    window.document.querySelector('.site-header')?.remove()
    window.document.querySelector('.site-footer')?.remove()
    return
  }

  root.setAttribute('data-react-root', 'true')

  const legacyPage = buildFromRoot(root)

  createRoot(root).render(
    <StrictMode>
      <LegacyPageProvider value={legacyPage}>
        <ApolloProvider client={client}>
          <DesignateBound element={root} isRoot={Component === 'root'} />
          {Component === 'root' && <RoutingProvider />} 
          {Component !== 'root' && Component} 
        </ApolloProvider>
      </LegacyPageProvider>
    </StrictMode>,
  )
}

